import { ShopInstance, IDCoreInstance } from '@/store'
import { setExperimentUser } from '@/utils/experiment'
import { setCookieShop, updateCookieShop } from '@/support'
import { catchError } from '@/utils/catchError'
import Cookies from 'js-cookie'
import { identify } from '@fullstory/browser'
import { version } from '@/../package.json'
import { identify as identifyAppcues } from '../../vendor/appcues'

import { ProfileCore } from 'profile-ts'

const ProfileInstance = new ProfileCore({
  environment: process.env.VUE_APP_CORE_ENV,
  theme: process.env.VUE_APP_THEME
})

const profile = {
  state: {
    user: {},
    profile: undefined,
    pref: ProfileInstance?.me.getPreferences(),
    theme: '',
    onboarding: { show_greeting: false, order_created: true, step: 0 },
    order_created: true
  },
  getters: {
    pref (state) {
      return state.pref
    },
    theme (state) {
      return state.theme
    },
    user (state) {
      return state.user
    },
    isEmployee (state) {
      return state?.profile?.user?.is_employee
    },
    userRole (state) {
      if (state?.profile?.user?.is_employee) {
        return state.profile.user.employee_type
      } else if (state.profile) {
        return state.profile.merchant.account.role
      }
      return { key: '', value: '' }
    },
    userType (state) {
      if (state?.profile?.user?.is_employee) {
        return state.profile.user.employee_type
      } else if (state.profile) {
        return state.profile.merchant.type
      }
      return { key: '', value: '' }
    },
    profile (state) {
      return state.profile
    },
    onboarding (state) {
      return state.onboarding
    },
    order_created (state) {
      return state.order_created
    },
    business_model (state) {
      return state.profile.merchant.account.business_model
    }
  },
  mutations: {
    'SET_USER' (state, value) {
      state.user = value
    },
    'SET_PROFILE' (state, profile) {
      state.profile = profile
    },
    'SET_PREF' (state, value) {
      state.pref = value
    },
    'SET_THEME_MODE' (state, value) {
      state.theme = value
    },
    'SET_SHOW_GREETING' (state, value) {
      state.onboarding.show_greeting = value
    },
    'SET_INITIAL_ORDER_CREATED' (state, value) {
      state.order_created = value
    },
    'SET_ORDER_CREATED' (state, value) {
      state.onboarding.order_created = value
    },
    'SET_ONBOARDING' (state, value) {
      state.onboarding = Object.assign(state.onboarding, value)
    },
    'SET_ONBOARDING_STEP' (state, value) {
      state.onboarding.step = value
    },
    'IDENTIFY_FS' (state) {
      const userVars = {
        id: state.profile.user.id,
        displayName: `${state.profile.user.first_name} ${state.profile.user.last_name}`,
        email: state.profile.user.email,
        merchantId_str: state.profile.merchant ? state.profile.merchant.id : null,
        merchantName_str: state.profile.merchant ? state.profile.merchant.name : null,
        userType_str: state.profile.merchant ? state.profile.merchant.type.key : null,
        appVersion_str: version
      }
      identify(state.profile.user.id, userVars)
    },
    'IDENTIFY_APPCUES' (state) {
      const { user, merchant } = state.profile
      const userVars = {
        userId: user.id,
        userEmail: user.email,
        userName: `${user.first_name} ${user.last_name}`,
        merchantId: merchant?.id
      }
      identifyAppcues(userVars)
    }
  },
  actions: {
    'GET_AUTHORIZATION' (context, params = {}) {
      return ProfileInstance.authorization.get(params, 'shop', process.env.VUE_APP_PROFILE_URL)
        .then((res) => {
          if (window.newrelic) {
            window.newrelic.interaction()
              .setAttribute('id', res.user.id)
              .setAttribute('username', `${res.user.first_name} ${res.user.last_name}`)
              .setAttribute('email', res.user.email)
              .setAttribute('merchant_id', res.merchant.id)
          }
          if (res.merchant) {
            setExperimentUser(res.merchant.account.id)
            context.commit('SET_USER', res.user)
            context.commit('SET_PROFILE', res)
            context.commit('SET_ACCOUNT', res.merchant.account)
            context.commit('SET_MERCHANT', res.merchant)
            context.commit('SET_ACCOUNT_AND_MERCHANT_INFORMATION', res)
            context.commit('SET_TEST_ACCOUNTS', { items: [], pagination: [] })
            context.dispatch('GET_ACCOUNT_SUMMARY', { merchant_id: res.merchant.id, account_id: res.merchant.account.id })
            if (!res.user.is_root && !res.user.is_employee) { context.dispatch('GET_ONBOARDING') }
            if (['production'].includes(process.env.VUE_APP_CORE_ENV)) {
              context.commit('IDENTIFY_FS')
              context.commit('IDENTIFY_APPCUES')
            }
          }
          return Promise.resolve(res)
        })
        .catch(err => {
          context.commit('toggleGlobalLoading', false)
          catchError(err, context)
        })
    },
    'GET_PREFERENCES' (context) {
      const pref = ProfileInstance.me.getPreferences()
      context.commit('SET_PREF', pref)
      return pref
    },
    'GET_ONBOARDING' (context) {
      const envAcronym = ShopInstance.envAcronym
      let cookieShop = Cookies.get(`${envAcronym}mp_shop`)

      if (cookieShop) {
        Promise.resolve(atob(cookieShop)).then(JSON.parse).then(resCookieParse => {
          cookieShop = resCookieParse
          if (cookieShop.onboarding && Object.entries(cookieShop.onboarding).length) {
            context.commit('SET_ONBOARDING', cookieShop.onboarding)
            context.commit('SET_INITIAL_ORDER_CREATED', cookieShop.onboarding.order_created)
          } else {
            return ProfileInstance.onboarding.get('shop')
              .then(res => {
                if (res && Object.entries(res).length) {
                  context.commit('SET_ONBOARDING', res)
                  context.commit('SET_INITIAL_ORDER_CREATED', res.order_created)
                } else {
                  context.commit('SET_ONBOARDING', { show_greeting: true })
                  context.commit('SET_INITIAL_ORDER_CREATED', false)
                }

                setCookieShop({ onboarding: { ...res } })
                return Promise.resolve(res)
              })
              .catch(err => {
                return catchError(err, context)
              })
          }
          return Promise.resolve(cookieShop.onboarding)
        })
      } else {
        return ProfileInstance.onboarding.get('shop')
          .then(res => {
            if (res && Object.entries(res).length) {
              context.commit('SET_ONBOARDING', res)
              context.commit('SET_INITIAL_ORDER_CREATED', res.order_created)
            } else {
              context.commit('SET_ONBOARDING', { show_greeting: true })
              context.commit('SET_INITIAL_ORDER_CREATED', false)
            }

            setCookieShop({ onboarding: { ...res } })

            return Promise.resolve(res)
          })
          .catch(err => {
            return catchError(err, context)
          })
      }
    },
    'SET_LANGUAGE' ({ dispatch, state }, lang) {
      const preferences = Object.assign({}, JSON.parse(JSON.stringify(state.pref)))
      preferences.language = lang
      dispatch('SET_PREFERENCES', preferences)
    },
    'SET_NEW_THEME_MODE' ({ dispatch, state }, mode) {
      const preferences = Object.assign({}, JSON.parse(JSON.stringify(state.pref)))
      preferences.appearance.mode = mode
      dispatch('SET_PREFERENCES', preferences)
    },
    'SET_PREFERENCES' (context, preferences) {
      try {
        preferences.appearance.theme = process.env.VUE_APP_THEME
        ProfileInstance.me.setPreferences(preferences)
        context.commit('SET_PREF', preferences)
        context.commit('SET_THEME_MODE', preferences.appearance.mode)
        return true
      } catch (err) {
        catchError(err, context)
      }
    },
    'FORCE_LIGHT_MODE' ({ state, dispatch }) {
      const preferences = Object.assign({}, JSON.parse(JSON.stringify(state.pref)))
      preferences.appearance.mode = 'light'
      dispatch('SET_PREFERENCES', preferences)
    },
    'SET_INITIAL_ORDER_CREATED' (context, value) {
      context.commit('SET_INITIAL_ORDER_CREATED', value)
      context.dispatch('UPDATE_ONBOARDING')
    },
    'UPDATE_ONBOARDING' (context) {
      updateCookieShop('onboarding', { onboarding: { ...context.state.onboarding } })
      return ProfileInstance.onboarding.update('shop', context.state.onboarding)
    },
    'UPDATE_ONBOARDING_STEP' (context, step) {
      context.commit('SET_ONBOARDING_STEP', step)
      context.dispatch('UPDATE_ONBOARDING')
    },
    'CLOSE_ORDER_CREATED' (context) {
      context.commit('SET_ORDER_CREATED', true)
      context.dispatch('UPDATE_ONBOARDING')
    },
    'CLOSE_GREETING' (context) {
      context.commit('SET_SHOW_GREETING', false)
      context.commit('SET_ORDER_CREATED', false)
      context.dispatch('UPDATE_ONBOARDING')
    },
    'DELETE_AUTHORIZATION' (context, logout = false) {
      return IDCoreInstance.auth.connect
        .revocationToken()
        .then(() => {
          /* eslint-disable-next-line no-console */
          console.log('Revocation token sent')
        })
        .catch(err => catchError(err, context))
        .finally(() => {
          ProfileInstance.authorization.deleteCookies('shop', logout)
          window.location.assign(process.env.VUE_APP_DEFAULT_REDIRECT)
          return Promise.resolve()
        })
    }
  }
}

export default profile
